// ** Modules
import NextLink from 'next/link';

// ** Types
import type { SxProps, Theme } from '@mui/material/styles';
import type { LinkProps as NextLinkProps } from 'next/link';
import type { FC, ReactNode, AnchorHTMLAttributes } from 'react';

// Define our custom logic props
type CustomLinkProps = {
	underline?: boolean;
	display?: string;
	fullWidth?: boolean;
	sx?: SxProps<Theme>;
	children?: ReactNode;
};

// Merge everything: Next.js Link props + Our Props + Standard HTML Anchor props
type CombinedLinkProps = NextLinkProps &
	CustomLinkProps &
	Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof NextLinkProps | 'sx'>;

const Link: FC<CombinedLinkProps> = ({
	id,
	as,
	children,
	href,
	replace,
	scroll,
	shallow,
	prefetch,
	target,
	display = 'inline-block',
	fullWidth = false,
	underline = false,
	sx,
	style,
	className,
	...props
}) => {
	const isExternalLink = typeof href === 'string' && /^https?:\/\//.test(href);
	const relAttribute = target === '_blank' && isExternalLink ? 'noopener noreferrer' : undefined;

	return (
		<NextLink
			href={href}
			as={as}
			replace={replace}
			scroll={scroll}
			shallow={shallow}
			prefetch={prefetch}
			id={id}
			target={target}
			rel={relAttribute}
			className={className}
			style={{
				display: display,
				width: fullWidth ? '100%' : 'auto',
				textDecoration: underline ? 'underline' : 'none',
				cursor: 'pointer',
				transition: '0.2s ease',
				...style,
				...(sx as any)
			}}
			{...props}
		>
			{children}
		</NextLink>
	);
};

Link.displayName = 'Link';

export default Link;
